import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Alert,
  Badge,
  Box,
  Button,
  Checkbox,
  Chip,
  IconButton,
  Paper,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  Tooltip,
  Typography,
} from '@mui/material';
import MoveToInboxIcon from '@mui/icons-material/MoveToInbox';
import UnarchiveIcon from '@mui/icons-material/Unarchive';
import DeleteForeverIcon from '@mui/icons-material/DeleteForever';
import LinkOffIcon from '@mui/icons-material/LinkOff';
import AddIcon from '@mui/icons-material/Add';
import VpnKeyIcon from '@mui/icons-material/VpnKey';
import FolderSharedIcon from '@mui/icons-material/FolderShared';
import BugReportIcon from '@mui/icons-material/BugReport';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { format } from 'date-fns';
import PageHeader from '../components/common/PageHeader';
import TableSkeleton from '../components/common/TableSkeleton';
import ConfirmDialog from '../components/common/ConfirmDialog';
import SandboxImportDialog from './sandbox/SandboxImportDialog';
import FreigabeAddDialog from './sandbox/FreigabeAddDialog';
import { sandboxService } from '../services/sandboxService';
import { useAuth } from '../hooks/useAuth';
import { useNotification } from '../hooks/NotificationContext';
import { useSandboxFreigaben } from '../hooks/useSandboxFreigaben';
import { SandboxFindingListItem } from '../types';
import { extractApiError } from '../utils/errorUtils';
import { formatSeverityLower, getKritikalitaetChipSx } from '../utils/severity';
import { parseUtcDateTime } from '../utils/time';

type TabKey = 'aktiv' | 'archiviert' | 'freigaben';
type TabBadgeColor = 'primary' | 'default' | 'warning';

/**
 * Zaehler steht im Textfluss neben dem Label.
 * MUI-Badge ist absolut (right-Offset plus translate 50%) und ragt aus dem Tab.
 * overflow:hidden an Tab und Tabs-Scroller schneidet das ab, besonders am letzten
 * Tab und bei zweistelligen Zahlen. Inline-Style schlaegt die Badge-Klasse,
 * sonst bleibt position:absolute und die Bubble haengt weiter aus dem Tab.
 */
function CountTabLabel({ label, count, color }: { label: string; count: number; color: TabBadgeColor }) {
  return (
    <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 1, whiteSpace: 'nowrap' }}>
      <Box component="span">{label}</Box>
      {count > 0 && (
        <Badge
          badgeContent={count}
          color={color}
          sx={{ flexShrink: 0 }}
          slotProps={{
            badge: {
              style: {
                position: 'static',
                transform: 'none',
                top: 'auto',
                right: 'auto',
                bottom: 'auto',
                left: 'auto',
              },
            },
          }}
        />
      )}
    </Box>
  );
}

/**
 * Hauptaccount-Sicht auf die isolierte Umgebung:
 * Sandbox-Findings pruefen/importieren/loeschen und Lese-Freigaben verwalten.
 */
const IsolierteUmgebungPage: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { showSuccess, showError } = useNotification();
  const { freigaben, loading: freigabenLoading, remove: removeFreigabe } = useSandboxFreigaben();

  const [tab, setTab] = useState<TabKey>('aktiv');
  const [rows, setRows] = useState<SandboxFindingListItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedIds, setSelectedIds] = useState<number[]>([]);
  const [importTargets, setImportTargets] = useState<SandboxFindingListItem[]>([]);
  const [importOpen, setImportOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<SandboxFindingListItem | null>(null);
  const [addFreigabeOpen, setAddFreigabeOpen] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setRows(await sandboxService.list(true));
    } catch {
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const aktiv = useMemo(() => rows.filter((r) => !r.archiviert_am), [rows]);
  const archiviert = useMemo(() => rows.filter((r) => !!r.archiviert_am), [rows]);

  const handleRestore = async (r: SandboxFindingListItem) => {
    try {
      await sandboxService.restore(r.id);
      showSuccess('Finding wiederhergestellt');
      load();
    } catch (e) {
      showError(extractApiError(e, 'Wiederherstellen fehlgeschlagen'));
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    try {
      await sandboxService.delete(deleteTarget.id);
      showSuccess('Finding endgültig gelöscht');
      setDeleteTarget(null);
      load();
    } catch (e) {
      showError(extractApiError(e, 'Löschen fehlgeschlagen'));
    }
  };

  const renderFindings = (list: SandboxFindingListItem[], archivedView: boolean) => {
    const allSelected = list.length > 0 && list.every((r) => selectedIds.includes(r.id));
    const someSelected = list.some((r) => selectedIds.includes(r.id));
    const selectedRows = list.filter((r) => selectedIds.includes(r.id));
    return (
    <>
      {selectedRows.length > 0 && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1.5, flexWrap: 'wrap' }} data-testid="sandbox-batch-bar">
          <Typography variant="body2" data-testid="sandbox-batch-count">{selectedRows.length} ausgewählt</Typography>
          <Button
            variant="contained"
            color="success"
            size="small"
            startIcon={<MoveToInboxIcon />}
            data-testid="sandbox-batch-import"
            onClick={() => {
              setImportTargets(selectedRows);
              setImportOpen(true);
            }}
          >
            In Audit importieren
          </Button>
          <Button size="small" onClick={() => setSelectedIds([])} sx={{ color: 'text.secondary' }}>
            Auswahl aufheben
          </Button>
        </Box>
      )}
    <TableContainer component={Paper}>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell padding="checkbox">
              <Checkbox
                size="small"
                checked={allSelected}
                indeterminate={someSelected && !allSelected}
                disabled={list.length === 0}
                onChange={() => setSelectedIds(allSelected ? [] : list.map((r) => r.id))}
                inputProps={{ 'aria-label': 'Alle Findings auswählen' }}
                data-testid="sandbox-select-all"
              />
            </TableCell>
            <TableCell>Titel</TableCell>
            <TableCell>Kritikalität</TableCell>
            <TableCell>CVSS</TableCell>
            <TableCell>Host</TableCell>
            <TableCell>Klasse</TableCell>
            <TableCell>{archivedView ? 'Archiviert am' : 'Erstellt'}</TableCell>
            <TableCell align="right">Aktionen</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {list.length === 0 && (
            <TableRow>
              <TableCell colSpan={8}>
                <Typography color="text.secondary" sx={{ py: 3, textAlign: 'center' }}>
                  {archivedView ? 'Keine archivierten Findings.' : 'Keine Findings in der isolierten Umgebung.'}
                </Typography>
              </TableCell>
            </TableRow>
          )}
          {list.map((r) => {
            const ts = archivedView ? r.archiviert_am : r.erstellt_am;
            return (
              <TableRow key={r.id} hover sx={{ cursor: 'pointer' }} onClick={() => navigate(`/isolierte-umgebung/findings/${r.id}`)}>
                <TableCell padding="checkbox" onClick={(e) => e.stopPropagation()}>
                  <Checkbox
                    size="small"
                    checked={selectedIds.includes(r.id)}
                    onChange={() => setSelectedIds((prev) => (prev.includes(r.id) ? prev.filter((id) => id !== r.id) : [...prev, r.id]))}
                    inputProps={{ 'aria-label': `${r.titel} auswählen` }}
                    data-testid={`sandbox-select-${r.id}`}
                  />
                </TableCell>
                <TableCell sx={{ fontWeight: 600 }}>{r.titel}</TableCell>
                <TableCell>
                  {r.kritikalitaet ? <Chip size="small" label={formatSeverityLower(r.kritikalitaet)} sx={getKritikalitaetChipSx(r.kritikalitaet)} /> : '–'}
                </TableCell>
                <TableCell>{r.cvss31_score != null ? r.cvss31_score.toFixed(1) : '–'}</TableCell>
                <TableCell>{r.host || '–'}</TableCell>
                <TableCell>{r.schwachstellenklasse?.name_de || '–'}</TableCell>
                <TableCell>{ts ? format(parseUtcDateTime(ts), 'dd.MM.yyyy HH:mm') : '–'}</TableCell>
                <TableCell align="right" onClick={(e) => e.stopPropagation()}>
                  <Tooltip title="In Audit importieren">
                    <IconButton
                      size="small"
                      color="success"
                      data-testid={`import-${r.id}`}
                      onClick={() => {
                        setImportTargets([r]);
                        setImportOpen(true);
                      }}
                    >
                      <MoveToInboxIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  {archivedView && (
                    <Tooltip title="Wiederherstellen">
                      <IconButton size="small" onClick={() => handleRestore(r)}>
                        <UnarchiveIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  )}
                  <Tooltip title="Endgültig löschen">
                    <IconButton size="small" color="error" onClick={() => setDeleteTarget(r)}>
                      <DeleteForeverIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </TableContainer>
    </>
    );
  };

  const renderFreigaben = () => (
    <>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5, flexWrap: 'wrap', gap: 1 }}>
        <Typography variant="body2" color="text.secondary">
          Diese Audits und Findings sind in Ihrer isolierten Umgebung aktuell <strong>lesbar</strong> (kein Schreibzugriff,
          keine Kunden-/Projekt-/Personendaten).
        </Typography>
        <Button variant="contained" startIcon={<AddIcon />} onClick={() => setAddFreigabeOpen(true)} data-testid="freigabe-add">
          Freigabe hinzufügen
        </Button>
      </Box>
      <TableContainer component={Paper}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Typ</TableCell>
              <TableCell>Name</TableCell>
              <TableCell>Audit</TableCell>
              <TableCell>Kunde / Projekt</TableCell>
              <TableCell>Freigegeben seit</TableCell>
              <TableCell align="right">Aktionen</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {freigaben.length === 0 && (
              <TableRow>
                <TableCell colSpan={6}>
                  <Typography color="text.secondary" sx={{ py: 3, textAlign: 'center' }}>Aktuell ist nichts freigegeben.</Typography>
                </TableCell>
              </TableRow>
            )}
            {freigaben.map((f) => (
              <TableRow key={f.id} hover>
                <TableCell>
                  {f.typ === 'audit' ? (
                    <Chip size="small" icon={<FolderSharedIcon />} label="Audit" color="info" variant="outlined" />
                  ) : (
                    <Chip size="small" icon={<BugReportIcon />} label="Finding" color="secondary" variant="outlined" />
                  )}
                </TableCell>
                <TableCell sx={{ fontWeight: 600 }}>{f.typ === 'audit' ? f.audit_name : f.schwachstelle_titel}</TableCell>
                <TableCell>{f.audit_name}</TableCell>
                <TableCell>{[f.kunde_name, f.projekt_name].filter(Boolean).join(' / ') || '–'}</TableCell>
                <TableCell>{format(parseUtcDateTime(f.erstellt_am), 'dd.MM.yyyy HH:mm')}</TableCell>
                <TableCell align="right">
                  <Tooltip title="Öffnen">
                    <IconButton size="small" onClick={() => navigate(f.typ === 'audit' ? `/audits/${f.audit_id}` : `/schwachstellen/${f.schwachstelle_id}`)}>
                      <OpenInNewIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Freigabe aufheben">
                    <IconButton
                      size="small"
                      color="warning"
                      data-testid={`freigabe-remove-${f.id}`}
                      onClick={async () => {
                        try {
                          await removeFreigabe(f.id);
                          showSuccess('Freigabe aufgehoben');
                        } catch (e) {
                          showError(extractApiError(e, 'Aufheben fehlgeschlagen'));
                        }
                      }}
                    >
                      <LinkOffIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </>
  );

  return (
    <Box>
      <PageHeader
        title="Isolierte Umgebung"
        subtitle="Findings aus dem eingeschränkten Zugang (AI-Tools) prüfen und importieren, Lese-Freigaben verwalten"
        actions={
          <Button variant="outlined" startIcon={<VpnKeyIcon />} onClick={() => navigate('/profil')}>
            Eingeschränkten Zugang verwalten
          </Button>
        }
      />

      {!user?.has_restricted_password && (
        <Alert severity="info" sx={{ mb: 2 }}>
          Für Ihren Account ist noch kein Restricted-Passwort gesetzt. Legen Sie es im Profil unter „Eingeschränkter Zugang“ an,
          damit AI-Tools Findings in dieser isolierten Umgebung anlegen können.
        </Alert>
      )}

      <Tabs
        value={tab}
        onChange={(_e, v) => { setTab(v); setSelectedIds([]); }}
        sx={{
          mb: 2,
          overflow: 'visible',
          '& .MuiTabs-scroller': { overflow: 'visible !important' },
          '& .MuiTab-root': { overflow: 'visible' },
        }}
      >
        <Tab value="aktiv" label={<CountTabLabel label="Aktiv" count={aktiv.length} color="primary" />} />
        <Tab value="archiviert" label={<CountTabLabel label="Archiviert" count={archiviert.length} color="default" />} />
        <Tab value="freigaben" label={<CountTabLabel label="Freigaben" count={freigaben.length} color="warning" />} data-testid="tab-freigaben" />
      </Tabs>

      {tab !== 'freigaben' && (loading ? <TableSkeleton /> : renderFindings(tab === 'aktiv' ? aktiv : archiviert, tab === 'archiviert'))}
      {tab === 'freigaben' && (freigabenLoading && freigaben.length === 0 ? <TableSkeleton /> : renderFreigaben())}

      {importOpen && importTargets.length > 0 && (
        <SandboxImportDialog
          open
          findings={importTargets.map((t) => ({ id: t.id, titel: t.titel }))}
          onClose={() => setImportOpen(false)}
          onImported={(res) => {
            const n = res.imported.length;
            const label = n === 1
              ? `„${importTargets[0]?.titel ?? 'Finding'}“ in „${res.audit_name}“ importiert`
              : `${n} Findings in „${res.audit_name}“ importiert`;
            setImportOpen(false);
            setImportTargets([]);
            setSelectedIds([]);
            showSuccess(label);
            load();
          }}
        />
      )}

      <ConfirmDialog
        open={!!deleteTarget}
        title="Finding endgültig löschen"
        message={`„${deleteTarget?.titel ?? ''}“ und alle zugehörigen Dateien werden unwiderruflich gelöscht.`}
        confirmText="Endgültig löschen"
        onConfirm={handleDelete}
        onCancel={() => setDeleteTarget(null)}
      />

      <FreigabeAddDialog open={addFreigabeOpen} onClose={() => setAddFreigabeOpen(false)} onCreated={() => setAddFreigabeOpen(false)} />
    </Box>
  );
};

export default IsolierteUmgebungPage;
